import React from 'react';
import styled from 'styled-components';
import { Card, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { colors } from 'styles';
import { Button } from 'components/basics';
import { breakpoint } from 'utils/mediaQueries';

const Container = styled(Card)`
  border: 2px solid ${colors.shades.grey.light};
  padding: ${({ isBilledAnnually, isMonthlySelected }) =>
    `0.5em 2em ${
      isBilledAnnually && !isMonthlySelected ? '0.55em' : '2em'
    } 2em`};
  text-align: center;
  width: 24.5em;
  box-shadow: none;
  ${breakpoint.down('xl')`
    margin-top: 1em !important;
  `}
`;

const Title = styled(Typography)`
  color: ${colors.purple};
  font-weight: bold;
`;

const Price = styled(Title)`
  color: ${colors.lightBlue};
`;
const Subtitle = styled(Title)`
  color: ${colors.grey};
`;

const CategoryContainer = styled.div`
  margin-top: 2em;
`;

const PlanItem = styled(Title)``;

const PlanCard = ({ plan, isMonthlySelected }) => {
  const getSubtitle = () => {
    if (plan.isCorporate) {
      return <br />;
    } else if (plan.pricePerComment) {
      return (
        <FormattedMessage
          id="pricing.syntheticBlock.perComment"
          values={{ price: `${parseFloat(plan.pricePerComment).toFixed(2)}€` }}
        />
      );
    } else if (isMonthlySelected) {
      return <FormattedMessage id="pricing.syntheticBlock.perMonth" />;
    } else {
      return (
        <FormattedMessage id="pricing.syntheticBlock.perMonthBilledAnnually" />
      );
    }
  };

  return (
    <Container
      isMonthlySelected={isMonthlySelected}
      isBilledAnnually={!!plan.billedAnnualPrice}
    >
      <Title variant="body1">{plan.name}</Title>
      <Price variant="body1">
        {plan[isMonthlySelected ? 'monthlyPrice' : 'annualPrice']}
        {plan.billedAnnualPrice ? '€' : ''}
      </Price>
      <Subtitle variant="caption">{getSubtitle()}</Subtitle>
      <CategoryContainer>
        <Subtitle variant="caption">
          <FormattedMessage id="pricing.syntheticBlock.projects" />
        </Subtitle>
        {!plan.pricePerComment && (
          <PlanItem variant="body2">
            {plan.isCorporate ? (
              <FormattedMessage id="pricing.syntheticBlock.unlimitedComments" />
            ) : (
              <FormattedMessage
                id="pricing.syntheticBlock.commentsPerPeriod"
                values={{
                  comments: isMonthlySelected
                    ? plan.commentsPerMonth
                    : plan.commentsPerYear,
                  period: isMonthlySelected ? (
                    <FormattedMessage id="pricing.syntheticBlock.month" />
                  ) : (
                    <FormattedMessage id="pricing.syntheticBlock.year" />
                  ),
                }}
              />
            )}
          </PlanItem>
        )}
        <PlanItem variant="body2">
          <FormattedMessage
            id="pricing.syntheticBlock.dataCollection"
            values={{ dataCollection: plan.dataCollection }}
          />
        </PlanItem>
        <PlanItem>
          <FormattedMessage
            id="pricing.syntheticBlock.maxCategories"
            values={{ maxCategories: plan.categoriesMax }}
          />
        </PlanItem>
        {plan.customerJourney && (
          <PlanItem>
            <FormattedMessage
              id="pricing.syntheticBlock.customerJourney"
              values={{ customerJourney: plan.customerJourney }}
            />
          </PlanItem>
        )}
        {plan.pricePerComment && (
          <>
            <PlanItem>
              <br />
            </PlanItem>
            <PlanItem>
              <br />
            </PlanItem>
          </>
        )}
      </CategoryContainer>
      <CategoryContainer>
        <Subtitle variant="caption">
          <FormattedMessage id="pricing.syntheticBlock.features" />
        </Subtitle>
        <PlanItem>
          {plan.isCorporate ? (
            <FormattedMessage id="pricing.syntheticBlock.unlimitedUsers" />
          ) : (
            <>
              {plan.userCount}{' '}
              <FormattedMessage id="pricing.syntheticBlock.user" />
              {plan.userCount > 1 ? 's' : ''}
            </>
          )}
        </PlanItem>
        <PlanItem>
          {plan.isCorporate ? (
            <FormattedMessage id="pricing.syntheticBlock.unlimitedBenchmarks" />
          ) : (
            <>
              {plan.benchmarkCount}{' '}
              <FormattedMessage id="pricing.syntheticBlock.benchmark" />
              {plan.benchmarkCount > 1 ? 's' : ''}
            </>
          )}
        </PlanItem>
        <PlanItem>
          {plan.isCorporate ? (
            <FormattedMessage id="pricing.syntheticBlock.unlimitedReportings" />
          ) : (
            <>
              {plan.reportingCount}{' '}
              <FormattedMessage id="pricing.syntheticBlock.reporting" />
            </>
          )}
        </PlanItem>
      </CategoryContainer>
      <CategoryContainer>
        <Subtitle variant="caption">
          <FormattedMessage id="pricing.syntheticBlock.support" />
        </Subtitle>
        <PlanItem>
          {/* Support via ticket : priorité {plan.supportPriority} */}
          <FormattedMessage
            id="pricing.syntheticBlock.ticketSupport"
            values={{
              priorityLevel: plan.supportPriority,
            }}
          />
          {plan.liveSupport && '+Live'}
        </PlanItem>
      </CategoryContainer>
      {plan.isCorporate ? (
        <Button
          color="primary"
          customTextColor={colors.white}
          customBackgroundColor={colors.lightBlue}
          customHoverBackgroundColor={colors.shades.lightBlue.dark}
          customHoverTextColor={colors.white}
          variant="contained"
          mt={2}
        >
          <FormattedMessage id="pricing.contactUs" />
        </Button>
      ) : (
        <Button
          color="primary"
          customTextColor={colors.white}
          customBackgroundColor={colors.lightBlue}
          customHoverBackgroundColor={colors.shades.lightBlue.dark}
          customHoverTextColor={colors.white}
          variant="contained"
          mt={2}
          onClick={() => window.open('https://www.cxinsights.io/')}
        >
          <FormattedMessage id="pricing.choosePlan" />
        </Button>
      )}
      {plan.billedAnnualPrice && !isMonthlySelected && (
        <Subtitle variant="caption">
          <FormattedMessage
            id="pricing.syntheticBlock.billedAt"
            values={{ price: `${plan.billedAnnualPrice}€` }}
          />
        </Subtitle>
      )}
    </Container>
  );
};

export default PlanCard;
